// MARK://dhk8/pos/pages/floor/WaitlistPanel
// Waitlist (lista de espera) panel docked on the right side of FloorPage.
// FIFO ordering by `position`. Host can:
//   - Add new walk-in party (mini form)
//   - "Notificar" → opens wa.me deep-link AND marks NOTIFIED
//   - "Sentar" → parent picks a table → POST /waitlist/:id/seat (opens session)
//   - "Se fue" → mark LEFT

import { useEffect, useState } from 'react'
import { Plus, Phone, X, RefreshCw, Users, MessageCircle } from 'lucide-react'
import {
  listWaitlist,
  addWaitlistEntry,
  notifyWaitlistEntry,
  leaveWaitlistEntry,
  waMeLink,
  type WaitlistEntryDto,
  type CreateWaitlistInput,
} from '../../lib/clients/floor.js'
import { useConfirm } from '../../components/ConfirmDialog.js'

const POLL_MS = 30_000

function fmtElapsed(iso: string): string {
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000))
  if (mins < 60) return `${mins}m`
  return `${Math.floor(mins / 60)}h ${mins % 60}m`
}

export interface WaitlistPanelProps {
  /** branch name shown in wa.me message — falls back to default text. */
  branchLabel?: string
  /** When user taps "Sentar", parent should ask for a table + call the seat API. */
  onSeatEntry?: (entry: WaitlistEntryDto) => Promise<void> | void
}

export default function WaitlistPanel({ branchLabel, onSeatEntry }: WaitlistPanelProps) {
  const confirm = useConfirm()
  const [items, setItems] = useState<WaitlistEntryDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  async function reload() {
    try {
      setError(null)
      const r = await listWaitlist()
      setItems(r)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar fila')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
    const iv = setInterval(() => void reload(), POLL_MS)
    return () => clearInterval(iv)
  }, [])

  async function handleNotify(entry: WaitlistEntryDto) {
    // Open wa.me deep-link FIRST so the mesero confirms the WhatsApp send
    // in their device, and ONLY then record NOTIFIED.
    if (entry.phone) {
      const msg = `Hola ${entry.name}, ya hay mesa lista${branchLabel ? ` en ${branchLabel}` : ''}. ¡Te esperamos!`
      window.open(waMeLink(entry.phone, msg), '_blank', 'noopener,noreferrer')
    }
    try {
      await notifyWaitlistEntry(entry.id)
      await reload()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al notificar')
    }
  }

  async function handleLeave(entry: WaitlistEntryDto) {
    if (!(await confirm({ title: 'Marcar como "se fue"', message: `¿Marcar a ${entry.name} como "se fue"?`, confirmLabel: 'Marcar', tone: 'danger' }))) return
    await leaveWaitlistEntry(entry.id)
    await reload()
  }

  async function handleSeatClick(entry: WaitlistEntryDto) {
    if (onSeatEntry) await onSeatEntry(entry)
    await reload()
  }

  return (
    <aside className="dhk8-waitlist">
      <header className="dhk8-waitlist__head">
        <h2 className="dhk8-waitlist__title">Lista de espera</h2>
        <div className="dhk8-waitlist__head-actions">
          <button type="button" className="dhk8-waitlist__icon-btn" title="Recargar" onClick={() => void reload()}>
            <RefreshCw size={14} />
          </button>
          <button type="button" className="dhk8-waitlist__cta" onClick={() => setCreating(true)}>
            <Plus size={14} /> Espera
          </button>
        </div>
      </header>

      {error && <div className="dhk8-waitlist__err">{error}</div>}
      {loading && <div className="dhk8-waitlist__hint">Cargando…</div>}
      {!loading && items.length === 0 && (
        <div className="dhk8-waitlist__empty">Nadie esperando.</div>
      )}

      <ol className="dhk8-waitlist__list">
        {items.map((entry, idx) => (
          <li key={entry.id} className={`dhk8-waitlist__card status-${entry.status.toLowerCase()}`}>
            <div className="dhk8-waitlist__card-row">
              <span className="dhk8-waitlist__pos">#{idx + 1}</span>
              <span className="dhk8-waitlist__elapsed">{fmtElapsed(entry.addedAt)}</span>
              {entry.status === 'NOTIFIED' && (
                <span className="dhk8-waitlist__badge tone-warn">Notificado</span>
              )}
            </div>
            <div className="dhk8-waitlist__card-row dhk8-waitlist__name">
              <strong>{entry.name}</strong>
              <span className="dhk8-waitlist__party"><Users size={11} /> {entry.partySize}</span>
            </div>
            {entry.phone && (
              <div className="dhk8-waitlist__phone"><Phone size={11} /> {entry.phone}</div>
            )}
            {entry.estimatedWaitMin !== null && (
              <div className="dhk8-waitlist__quoted">Estimado: {entry.estimatedWaitMin} min</div>
            )}
            {entry.notes && <div className="dhk8-waitlist__notes">{entry.notes}</div>}
            <div className="dhk8-waitlist__card-actions">
              <button
                type="button"
                className="dhk8-waitlist__btn primary"
                onClick={() => void handleNotify(entry)}
                disabled={!entry.phone}
                title={entry.phone ? 'Notificar por WhatsApp' : 'Sin teléfono'}
              >
                <MessageCircle size={12} /> {entry.status === 'NOTIFIED' ? 'Re-notif' : 'Notificar'}
              </button>
              <button type="button" className="dhk8-waitlist__btn primary" onClick={() => void handleSeatClick(entry)}>
                Sentar
              </button>
              <button type="button" className="dhk8-waitlist__btn danger" onClick={() => void handleLeave(entry)} title="Se fue">
                <X size={12} />
              </button>
            </div>
          </li>
        ))}
      </ol>

      {creating && (
        <CreateWaitlistForm
          onCancel={() => setCreating(false)}
          onCreated={async () => {
            setCreating(false)
            await reload()
          }}
        />
      )}

      <style>{styles}</style>
    </aside>
  )
}

function CreateWaitlistForm({
  onCancel,
  onCreated,
}: {
  onCancel: () => void
  onCreated: () => Promise<void> | void
}) {
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [partySize, setPartySize] = useState(2)
  const [estimatedWaitMin, setEstimatedWaitMin] = useState<number | ''>('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) return setErr('Nombre requerido')
    setBusy(true)
    setErr(null)
    try {
      const input: CreateWaitlistInput = { name: name.trim(), partySize }
      if (phone.trim()) input.phone = phone.trim()
      if (typeof estimatedWaitMin === 'number') input.estimatedWaitMin = estimatedWaitMin
      await addWaitlistEntry(input)
      await onCreated()
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="dhk8-waitlist__modal-bg" onClick={onCancel}>
      <form
        className="dhk8-waitlist__modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => void handleSubmit(e)}
      >
        <h3>Agregar a fila</h3>
        <label>
          <span>Nombre</span>
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </label>
        <label>
          <span>Teléfono (recomendado, para WhatsApp)</span>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+52..." />
        </label>
        <div className="dhk8-waitlist__modal-row">
          <label>
            <span>Personas</span>
            <input
              type="number"
              min={1}
              max={50}
              value={partySize}
              onChange={(e) => setPartySize(Math.max(1, Number(e.target.value) || 1))}
            />
          </label>
          <label>
            <span>Espera (min, opcional)</span>
            <input
              type="number"
              min={1}
              value={estimatedWaitMin}
              onChange={(e) => setEstimatedWaitMin(e.target.value === '' ? '' : Math.max(1, Number(e.target.value)))}
            />
          </label>
        </div>
        {err && <div className="dhk8-waitlist__err">{err}</div>}
        <div className="dhk8-waitlist__modal-actions">
          <button type="button" onClick={onCancel} disabled={busy}>Cancelar</button>
          <button type="submit" className="primary" disabled={busy}>{busy ? 'Guardando…' : 'Agregar'}</button>
        </div>
      </form>
    </div>
  )
}

const styles = `
  .dhk8-waitlist {
    display: flex; flex-direction: column; gap: 6px;
    width: 260px; min-width: 220px; max-width: 300px;
    padding: 10px; overflow-y: auto;
    background: var(--bg-surface, #0f0f0f); border: 1px solid var(--border, #2a2a2a);
    border-radius: var(--radius, 4px); color: var(--ink, #f0f0f0);
    font-family: var(--font-mono, monospace); font-size: 12px;
  }
  .dhk8-waitlist__head { display: flex; align-items: center; justify-content: space-between; gap: 6px; padding-bottom: 6px; border-bottom: 1px solid var(--border, #2a2a2a); }
  .dhk8-waitlist__title { margin: 0; font-size: 13px; font-weight: 600; }
  .dhk8-waitlist__head-actions { display: flex; gap: 4px; }
  .dhk8-waitlist__icon-btn, .dhk8-waitlist__cta {
    background: transparent; color: var(--ink-2, #aaa); border: 1px solid var(--border, #2a2a2a);
    border-radius: var(--radius-xs); padding: 4px 8px; cursor: pointer; font: inherit; display: inline-flex; align-items: center; gap: 4px;
  }
  .dhk8-waitlist__cta { color: var(--accent, #e87840); border-color: var(--accent, #e87840); }
  .dhk8-waitlist__icon-btn:hover, .dhk8-waitlist__cta:hover { background: rgba(232,120,64,0.08); }
  .dhk8-waitlist__list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
  .dhk8-waitlist__card { background: var(--bg, #181818); border: 1px solid var(--border, #2a2a2a); border-radius: var(--radius-xs); padding: 6px 8px; display: flex; flex-direction: column; gap: 4px; }
  .dhk8-waitlist__card.status-notified { border-color: rgba(224,162,60,.4); }
  .dhk8-waitlist__card-row { display: flex; align-items: center; justify-content: space-between; gap: 6px; }
  .dhk8-waitlist__pos { font-weight: 700; color: var(--accent, #e87840); }
  .dhk8-waitlist__elapsed { font-size: 11px; color: var(--ink-3, #888); }
  .dhk8-waitlist__name strong { font-size: 13px; }
  .dhk8-waitlist__party { display: inline-flex; align-items: center; gap: 4px; color: var(--ink-2, #aaa); }
  .dhk8-waitlist__phone { color: var(--ink-2, #aaa); font-size: 11px; display: inline-flex; align-items: center; gap: 4px; }
  .dhk8-waitlist__quoted { font-size: 11px; color: var(--ink-3, #888); }
  .dhk8-waitlist__notes { font-size: 11px; color: var(--ink-3, #888); font-style: italic; }
  .dhk8-waitlist__badge { font-size: 9px; padding: 1px 5px; border-radius: var(--radius-xs); text-transform: uppercase; letter-spacing: .04em; }
  .dhk8-waitlist__badge.tone-warn { background: rgba(224,162,60,.18); color: #E0A23C; }
  .dhk8-waitlist__card-actions { display: flex; gap: 4px; flex-wrap: wrap; margin-top: 2px; }
  .dhk8-waitlist__btn { background: transparent; color: var(--ink-2, #aaa); border: 1px solid var(--border, #2a2a2a); border-radius: var(--radius-xs); padding: 3px 7px; cursor: pointer; font: inherit; font-size: 11px; display: inline-flex; align-items: center; gap: 3px; }
  .dhk8-waitlist__btn:hover:not(:disabled) { color: var(--ink, #fff); }
  .dhk8-waitlist__btn:disabled { opacity: .4; cursor: not-allowed; }
  .dhk8-waitlist__btn.primary { color: var(--accent, #e87840); border-color: var(--accent, #e87840); }
  .dhk8-waitlist__btn.danger { color: #ef4444; border-color: #5a2222; }
  .dhk8-waitlist__err { color: #ef4444; font-size: 11px; padding: 4px 6px; }
  .dhk8-waitlist__hint { color: var(--ink-3, #888); font-size: 11px; padding: 4px 6px; }
  .dhk8-waitlist__empty { color: var(--ink-3, #888); font-style: italic; padding: 12px 6px; text-align: center; }

  .dhk8-waitlist__modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.55); display: flex; align-items: center; justify-content: center; z-index: 50; }
  .dhk8-waitlist__modal { background: var(--bg-surface, #181818); border: 1px solid var(--border, #2a2a2a); border-radius: var(--radius-sm); padding: 16px; width: 320px; display: flex; flex-direction: column; gap: 10px; color: var(--ink, #f0f0f0); font-family: var(--font-mono, monospace); font-size: 12px; }
  .dhk8-waitlist__modal h3 { margin: 0; font-size: 14px; }
  .dhk8-waitlist__modal label { display: flex; flex-direction: column; gap: 4px; }
  .dhk8-waitlist__modal label > span { font-size: 10px; text-transform: uppercase; letter-spacing: .06em; color: var(--ink-3, #888); }
  .dhk8-waitlist__modal input { background: var(--bg, #0f0f0f); color: var(--ink, #f0f0f0); border: 1px solid var(--border, #2a2a2a); border-radius: var(--radius-xs); padding: 6px 8px; font: inherit; }
  .dhk8-waitlist__modal-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .dhk8-waitlist__modal-actions { display: flex; justify-content: flex-end; gap: 6px; }
  .dhk8-waitlist__modal-actions button { background: transparent; color: var(--ink-2, #aaa); border: 1px solid var(--border, #2a2a2a); border-radius: var(--radius-xs); padding: 6px 12px; cursor: pointer; font: inherit; }
  .dhk8-waitlist__modal-actions button.primary { color: var(--accent, #e87840); border-color: var(--accent, #e87840); }
`
